import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Legend,
  CartesianGrid,
} from "recharts";
import { SECTOR_LABELS, STAGE_LABELS } from "../../data/mockData";
import { useAuth } from "../../context/authContext";
import { ROLES } from "../../constants/roles";
import { useFunderImpactHook } from "../../hooks/useFunderHooks";
import {
  Card,
  PageHeader,
  CHART_COLORS,
} from "../fundercomponents/funderComponents";
import "./impact.css";

/*
 * Funder — Impact & Outcomes
 * --------------------------
 * Deeper view of portfolio impact: sector performance, stage maturity,
 * gender comparison, and QoL change vs baseline.
 *
 * CSS class prefix for this page: `funderImpact-`.
 */
export default function Impact() {
  const { user, token } = useAuth();
  const isFunder = user?.role === ROLES.FUNDER;

  const { data: impactApiData, isLoading } = useFunderImpactHook({
    token,
    userId: user?.id,
    enabled: isFunder,
  });

  const impactData = impactApiData ?? {
    impact_by: {
      sector: [],
      gender_classification: [],
    },
    venture_stage_distribution: [],
    quality_of_life: {
      baseline_vs_current: [],
    },
    impact_growth_trend: [],
  };

  const sectorImpact = (impactData?.impact_by?.sector ?? []).map((row) => ({
    sector: SECTOR_LABELS[String(row.sector ?? "").toLowerCase()] ?? row.sector,
    jobs: Number(row.jobs_created ?? 0),
    beneficiaries: Number(row.beneficiaries ?? 0),
  }));

  const stageData = (impactData?.venture_stage_distribution ?? []).map(
    (row) => ({
      stage: STAGE_LABELS[String(row.stage ?? "").toLowerCase()] ?? row.stage,
      count: Number(row.count ?? 0),
    }),
  );

  const genderImpact = (impactData?.impact_by?.gender_classification ?? []).map(
    (row) => ({
      category: row.classification ?? "-",
      beneficiaries: Number(row.beneficiaries ?? 0),
    }),
  );

  const qolComparison = (
    impactData?.quality_of_life?.baseline_vs_current ?? []
  ).map((row) => ({
    indicator: row.indicator ?? "-",
    baseline: Number(row.baseline ?? 0),
    current: Number(row.current ?? 0),
  }));

  if (isLoading && isFunder) {
    return (
      <div className="funderImpact">
        <PageHeader
          title="Impact & Outcomes"
          description="Deep-dive into portfolio impact metrics."
        />
        <p className="admin-muted">Loading impact data...</p>
      </div>
    );
  }

  return (
    <div className="funderImpact">
      <PageHeader
        title="Impact & Outcomes"
        description="Deep-dive into portfolio impact metrics."
      />

      <Card
        title="Impact by Sector"
        description="Jobs and end customers across focus areas."
      >
        <div className="funderImpact-chart funderImpact-chart-lg">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={sectorImpact}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="sector" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar
                dataKey="jobs"
                name="Jobs Created"
                fill={CHART_COLORS[0]}
                radius={4}
              />
              <Bar
                dataKey="beneficiaries"
                name="End Customers"
                fill={CHART_COLORS[1]}
                radius={4}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="funderImpact-grid-2">
        <Card
          title="Venture Stage Distribution"
          description="Portfolio maturity breakdown."
        >
          <div className="funderImpact-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stageData} layout="vertical">
                <XAxis type="number" allowDecimals={false} />
                <YAxis type="category" dataKey="stage" width={80} />
                <Tooltip />
                <Bar
                  dataKey="count"
                  fill={CHART_COLORS[0]}
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card
          title="Impact by Gender Classification"
          description="End customers reached by leadership type."
        >
          <div className="funderImpact-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={genderImpact}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="category" />
                <YAxis />
                <Tooltip />
                <Bar
                  dataKey="beneficiaries"
                  fill={CHART_COLORS[2]}
                  radius={4}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="funderImpact-grid-2">
        <Card
          title="Quality of Life: Baseline vs Current"
          description="Aggregated indicator improvements."
        >
          <div className="funderImpact-chart">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart
                cx="50%"
                cy="50%"
                outerRadius="70%"
                data={qolComparison}
              >
                <PolarGrid />
                <PolarAngleAxis dataKey="indicator" />
                <PolarRadiusAxis angle={30} domain={[0, 5]} />
                <Radar
                  name="Baseline"
                  dataKey="baseline"
                  stroke="hsl(var(--muted-foreground))"
                  fill="hsl(var(--muted-foreground))"
                  fillOpacity={0.2}
                />
                <Radar
                  name="Current"
                  dataKey="current"
                  stroke={CHART_COLORS[0]}
                  fill={CHART_COLORS[0]}
                  fillOpacity={0.4}
                />
                <Legend />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </div>
  );
}
